import * as Api from '@ViewModels';
import { convertHtmlStringValueToPlainText } from '../../../../models/utils/stringUtils';

/** Serializes editor state to a stable string for change detection */
export function serializeContent(editorState: Api.IRichContentEditorState | undefined): string | null {
	if (!editorState) {
		return null;
	}
	try {
		return JSON.stringify(editorState.getRawRichTextContent());
	} catch {
		return null;
	}
}

// Derived from the stored HTML rather than getPlainTextPreview, which the editor computes differently from the
// converter used for saved content
function normalizedPlainText(editorState: Api.IRichContentEditorState | undefined) {
	const document = editorState?.getRawRichTextContent()?.document;
	return (document ? convertHtmlStringValueToPlainText(document) : '').replace(/\s+/g, ' ').trim();
}

export interface IContentBaseline {
	plainText: string;
	serializedContent: string | null;
	/** True until the first change to the text, while the editor may still be re-emitting reformatted saved content */
	settling: boolean;
	subject: string;
}

/**
 * Pass `settling: false` when the content was read back from the editor (e.g. what autosave just sent), since the
 * editor has nothing left to reformat and any later change is the user's.
 */
export function createContentBaseline(
	editorState: Api.IRichContentEditorState | undefined,
	subject: string | undefined,
	{ settling = true }: { settling?: boolean } = {}
): IContentBaseline {
	return {
		plainText: normalizedPlainText(editorState),
		serializedContent: serializeContent(editorState),
		settling,
		subject: subject ?? '',
	};
}

/**
 * The editor reports content changes it made itself (HTML normalization on load, default email fonts applied after
 * a SetContent), so right after a load or save a change that keeps the same text is adopted as the new baseline
 * instead of counted as an edit.
 */
export function reconcileContentChange(
	baseline: IContentBaseline,
	editorState: Api.IRichContentEditorState | undefined,
	subject: string | undefined
): { baseline: IContentBaseline; isDirty: boolean } {
	const nextSubject = subject ?? '';
	if (nextSubject !== baseline.subject) {
		return { baseline: { ...baseline, settling: false }, isDirty: true };
	}

	const serializedContent = serializeContent(editorState);
	if (serializedContent === baseline.serializedContent) {
		return { baseline, isDirty: false };
	}

	const plainText = normalizedPlainText(editorState);
	if (baseline.settling && plainText === baseline.plainText) {
		return { baseline: { ...baseline, plainText, serializedContent }, isDirty: false };
	}

	return { baseline: { ...baseline, settling: false }, isDirty: true };
}
